// mostly copied from https://github.com/josephj/react-icalendar-link
// direct usage is not flexible enough for our needs

export interface ICalEvent {
  title: string;
  startTime: string;
  description?: string;
  endTime?: string;
  location?: string;
  attendees?: string[];
  url?: string;
  wholeDay?: boolean;
}

export const formatDate = (
  dateString: string,
  dateOnly: boolean = false,
): string => {
  const dateTime = new Date(dateString);
  let offset = dateTime.getTimezoneOffset();
  if (!offset) {
    return dateTime.toISOString();
  }
  const finalDate = new Date(dateTime.getTime() - offset * 60 * 1000);
  const iso = finalDate
    .toISOString()
    .replace(/:\d{2}\.000Z$/, '00')
    .replaceAll(/[.:-]/g, '');
  return dateOnly ? iso.split('T')[0] : iso;
};

export const buildUrl = (
  event: ICalEvent,
  useDataURL: boolean = false,
  rawContent: string = '',
): string => {
  const body: string[] = [];

  if (!event || !event.startTime || !event.title)
    throw Error('Both startTime and title fields are mandatory');

  if (event.wholeDay) {
    body.push(`DTSTART;VALUE=DATE:${formatDate(event.startTime, true)}`);
  } else {
    body.push(`DTSTART:${formatDate(event.startTime)}`);
  }
  body.push(`SUMMARY:${event.title}`);

  event.url && body.push(`URL:${event.url}`);
  event.attendees &&
    event.attendees.forEach((attendee) => {
      const regExp = /^([^<]+)\s*<(.+)>/;
      const matches = attendee.match(regExp);
      if (matches) {
        const name = matches[1];
        const email = matches[2];
        body.push(
          [
            'ATTENDEE',
            `CN=${name}`,
            'CUTYPE=INDIVIDUAL',
            'PARTSTAT=NEEDS-ACTION',
            'ROLE=REQ-PARTICIPANT',
            `RSVP=TRUE:mailto:${email}`,
          ].join(';'),
        );
      }
    });

  if (event.endTime) {
    if (event.wholeDay) {
      body.push(`DTEND;VALUE=DATE:${formatDate(event.endTime, true)}`);
    } else {
      body.push(`DTEND:${formatDate(event.endTime)}`);
    }
  }
  event.description && body.push(`DESCRIPTION:${event.description}`);
  event.location && body.push(`LOCATION:${event.location}`);
  rawContent && body.push(rawContent);

  const url = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'BEGIN:VEVENT',
    body.join('\n'),
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\n');

  if (useDataURL) {
    return encodeURI(`data:text/calendar;charset=utf8,${url}`);
  } else {
    return url;
  }
};

export const downloadBlob = (blob: Blob, filename: string): void => {
  const linkEl = document.createElement('a');
  linkEl.href = window.URL.createObjectURL(blob);
  linkEl.setAttribute('download', filename);
  document.body.appendChild(linkEl);
  linkEl.click();
  document.body.removeChild(linkEl);
};

export const isIE = (): boolean => {
  return !!(
    typeof window !== 'undefined' &&
    // @ts-ignore
    window.navigator.msSaveOrOpenBlob &&
    window.Blob
  );
};

export const isIOSSafari = (): boolean => {
  const ua = window.navigator.userAgent;
  const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
  const webkit = !!ua.match(/WebKit/i);

  return iOS && webkit && !ua.match(/CriOS/i);
};

export const isIOSChrome = (): boolean => {
  const ua = window.navigator.userAgent;
  const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);

  return iOS && !!ua.match(/CriOS/i);
};

export const downloadICalEvent = (event: ICalEvent, filename: string): void => {
  const url: string = buildUrl(event, isIOSSafari());
  const blob: Blob = new Blob([url], {
    type: 'text/calendar;charset=utf-8',
  });

  // IE
  if (isIE()) {
    // @ts-ignore
    window.navigator?.msSaveOrOpenBlob(blob, filename);
    return;
  }

  // Safari
  if (isIOSSafari()) {
    window.open(url, '_blank');
    return;
  }

  // Desktop
  downloadBlob(blob, filename);
};
